import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { PORTFOLIO_ITEMS, IMAGES } from "@/data/content";
import { PROJECTS } from "@/data/projects";

const CATEGORIES = ["All", "High-End Rendering", "Custom Millwork", "Production System", "Workflow Improvement", "Storage Systems", "Architectural Millwork", "Interior Visualization", "Photorealistic Render", "Commercial Visualization"];

export default function Portfolio() {
  const [filter, setFilter] = useState("All");
  const items = filter === "All" ? PORTFOLIO_ITEMS : PORTFOLIO_ITEMS.filter((p) => p.type === filter);

  return (
    <div data-testid="page-portfolio">
      <PageHero
        overline="— Portfolio"
        title="Selected work."
        accent="In progress, always."
        body="A growing archive of high-end interior renderings, custom cabinetry, Cabinet Vision systems and workflow improvements. New case studies are added as client work clears confidentiality."
        image={PROJECTS[0]?.scenes?.[PROJECTS[0]?.coverScene || 0]?.src || IMAGES.portfolio1}
        testid="portfolio-hero"
      />

      {/* FEATURED PROJECTS (full case studies) */}
      {PROJECTS.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-24 pb-12" data-testid="featured-projects">
          <Reveal>
            <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
              <div>
                <div className="overline mb-3">— Featured projects</div>
                <h2 className="font-serif text-4xl md:text-5xl leading-tight">Recent case studies.</h2>
              </div>
              <p className="text-[#A3A3A3] max-w-md">Each project is a complete narrative — renders, materials, lighting and, where applicable, shop drawings and production documentation.</p>
            </div>
          </Reveal>

          <div className="space-y-12">
            {PROJECTS.map((p, idx) => {
              const cover = p.scenes[p.coverScene || 0];
              const isOdd = idx % 2 === 1;
              return (
                <Reveal key={p.slug} delay={idx * 80}>
                  <Link to={`/portfolio/${p.slug}`} data-testid={`project-${p.slug}`} className="group block">
                    <div className={`grid md:grid-cols-12 gap-6 items-center ${isOdd ? "md:[direction:rtl]" : ""}`}>
                      <div className="md:col-span-8 overflow-hidden border border-white/5 md:[direction:ltr]">
                        <img src={cover.src} alt={p.title} className="w-full aspect-[3/2] object-cover transition-transform duration-700 group-hover:scale-105" />
                      </div>
                      <div className="md:col-span-4 md:[direction:ltr]">
                        <div className="overline mb-3">{p.type} · {p.year}</div>
                        <h3 className="font-serif text-3xl md:text-4xl mb-3 text-[#EAEAEA] group-hover:text-[#B58B5D] transition-colors leading-tight">{p.title}</h3>
                        <p className="text-sm text-[#A3A3A3] leading-relaxed mb-5">{p.location}</p>
                        <p className="text-[15px] text-[#A3A3A3] leading-relaxed line-clamp-4">{p.summary}</p>
                        <div className="mt-6 flex items-center gap-2 text-[#B58B5D] text-xs font-mono uppercase tracking-[0.18em]">
                          View case study <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      {/* SELECTED VIGNETTES (visual archive) */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20" data-testid="archive">
        <Reveal>
          <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
            <div>
              <div className="overline mb-3">— Selected vignettes</div>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight">A wider archive.</h2>
            </div>
            <div className="flex flex-wrap gap-3" data-testid="portfolio-filters">
              {CATEGORIES.slice(0, 5).map((c) => (
                <button
                  key={c}
                  data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setFilter(c)}
                  className={`text-[11px] font-mono uppercase tracking-[0.18em] px-4 py-2 border transition-all ${
                    filter === c
                      ? "bg-[#B58B5D] border-[#B58B5D] text-[#0A0A0A]"
                      : "border-white/10 text-[#A3A3A3] hover:text-[#EAEAEA] hover:border-white/30"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="portfolio-grid">
          {items.map((p, i) => (
            <Reveal key={p.title + i} delay={i * 60}>
              <div data-testid={`portfolio-item-${i}`} className="dl-card group overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-90" />
                </div>
                <div className="p-6">
                  <div className="overline mb-2">{p.type}</div>
                  <h3 className="font-serif text-xl text-[#EAEAEA] group-hover:text-[#B58B5D] transition-colors">{p.title}</h3>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#0E0E0E] border-y border-white/5 py-24" data-testid="case-study">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="overline mb-3">— Case study</div>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight">From separate customer drawings to a <em className="text-[#B58B5D] not-italic">connected Cabinet Vision workflow.</em></h2>
          </Reveal>
          <div className="md:col-span-7 space-y-6 text-[#A3A3A3] leading-relaxed">
            <div>
              <div className="overline mb-2 text-[#B58B5D]">Background</div>
              <p>The company used separate software for customer drawings and production engineering, requiring approved projects to be recreated before manufacturing.</p>
            </div>
            <div>
              <div className="overline mb-2 text-[#B58B5D]">Challenge</div>
              <p>Duplicated labour, inconsistent dimensions, delayed production release and greater possibility of missed information.</p>
            </div>
            <div>
              <div className="overline mb-2 text-[#B58B5D]">Approach</div>
              <p>Reviewed the workflow from customer presentation through production. Developed a Cabinet Vision-based workflow supporting elevations, material callouts, cabinet identification, production construction, hardware assignment, reporting and CNC-ready output.</p>
            </div>
            <div>
              <div className="overline mb-2 text-[#B58B5D]">Outcome</div>
              <p className="text-[#EAEAEA]">A more coordinated relationship between design approval and manufacturing information — reusable cabinet standards, parametric dimensions, finished-end controls, drawer and hardware rules, drawing layers, production notes and report structures.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
